import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Alert, Badge, Button, Form, Nav, Spinner, Table } from 'react-bootstrap';
import OnlineOrders from './OnlineOrders';
import StoreReports from './StoreReports';
import { Link } from 'react-router-dom';
import useSectionTab from '../hooks/useSectionTab';
import { ProductThumb } from './ProductPhotos';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../utils/permissionUtils';

export const STORE_SECTIONS = ['orders', 'products', 'reports'];
const REPORT_ROLES = ['SALES_MGR', 'ORG_ADMIN']; // the report shows what the store earns: managers only

/** What is published on the online store: tick products to put them on the shop, untick to take them off. */
function ProductsOnline() {
  const { user } = useAuth();
  const canPublish = hasAnyRole(user, ['SALES_MGR', 'ORG_ADMIN']);
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState('');
  const [only, setOnly] = useState('all');
  const [busy, setBusy] = useState(false);
  const [slug, setSlug] = useState(null);
  const [byWeight, setByWeight] = useState(false); // shipping by weight: the weight of each product is asked for
  const money = useMemo(() => new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), []);

  const load = () => axios.get('/api/store/products').then((r) => setRows(r.data || [])).catch((e) => toast.error(e.response?.data?.message || 'Could not load products.'));
  useEffect(() => { load(); axios.get('/api/store/status').then((r) => { setSlug(r.data?.slug); setByWeight(Boolean(r.data?.shippingByWeight)); }).catch(() => {}); }, []);

  const shown = useMemo(() => {
    const words = search.toLowerCase().split(/\s+/).filter(Boolean);
    return (rows || []).filter((p) => (only === 'all' || (only === 'online' ? p.online : !p.online))
      && words.every((w) => `${p.name} ${p.sku || ''} ${p.category || ''}`.toLowerCase().includes(w)));
  }, [rows, search, only]);

  const saveWeight = async (p, text) => {
    const weight = text === '' ? null : Number(text);
    if (weight != null && (Number.isNaN(weight) || weight < 0 || weight > 1000)) { toast.warn('The weight must be between 0 and 1000 kg.'); return; }
    if ((p.weight ?? null) === weight || (p.weight != null && weight != null && Number(p.weight) === weight)) return;
    try {
      const r = await axios.put(`/api/store/products/${p.id}/weight`, { weight });
      setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, weight: r.data.weight } : x)));
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save the weight.');
    }
  };

  const publish = async (ids, online) => {
    setBusy(true);
    try {
      const r = await axios.put('/api/store/products/online', { ids, online });
      if ((r.data.refused || []).length) toast.warn(`Not published: ${r.data.refused.join('; ')}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not change that.');
    } finally {
      setBusy(false);
    }
  };

  if (!rows) return <div className="py-4 text-center"><Spinner animation="border" size="sm" /></div>;
  const onlineCount = rows.filter((p) => p.online).length;

  return (
    <div className="mt-3" data-testid="store-products">
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Form.Control type="search" placeholder="Search products" value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 280 }} />
        <Form.Select value={only} onChange={(e) => setOnly(e.target.value)} style={{ maxWidth: 200 }}>
          <option value="all">All products</option>
          <option value="online">On the store ({onlineCount})</option>
          <option value="off">Not on the store</option>
        </Form.Select>
        {canPublish && (
          <>
            <Button size="sm" variant="outline-primary" disabled={busy || shown.length === 0} onClick={() => publish(shown.map((p) => p.id), true)}>Publish these {shown.length}</Button>
            <Button size="sm" variant="outline-secondary" disabled={busy || shown.length === 0} onClick={() => publish(shown.map((p) => p.id), false)}>Take these off</Button>
          </>
        )}
        {slug && <a className="ms-auto" href={`/shop/${slug}`} target="_blank" rel="noreferrer">Open my store <i className="bi bi-box-arrow-up-right" /></a>}
      </div>
      {rows.length === 0
        ? <Alert variant="info">No products yet. Add them in <Link to="/org-admin/products">Org Admin &rarr; Products</Link>, with a price and photos.</Alert>
        : (
          <Table hover responsive size="sm" className="align-middle">
            <thead><tr><th style={{ width: 50 }}>On store</th><th /><th>Product</th><th>Category</th><th className="text-end">Price</th>{byWeight && <th className="text-end" style={{ width: 120 }}>Weight (kg)</th>}<th>Stock</th></tr></thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id}>
                  <td className="text-center">
                    <Form.Check type="checkbox" aria-label={`${p.name} on the store`} checked={p.online} disabled={!canPublish || busy || (!p.online && (!p.isActive || !(Number(p.price) > 0)))}
                      onChange={(e) => publish([p.id], e.target.checked)} />
                  </td>
                  <td style={{ width: 52 }}><ProductThumb productId={p.id} imageId={p.mainPhotoId} size={40} url={p.mainPhotoUrl || null} /></td>
                  <td>{p.name}{p.sku && <div className="small text-muted">{p.sku}</div>}{!p.isActive && <Badge bg="secondary" className="ms-1">Inactive</Badge>}</td>
                  <td>{p.category || ''}</td>
                  <td className="text-end">{Number(p.price) > 0 ? money.format(p.price) : <span className="text-muted">No price. <Link to="/org-admin/products" title="A product needs a price before it can be published">Set one</Link></span>}</td>
                  {byWeight && (
                    <td className="text-end">
                      {canPublish
                        ? <Form.Control size="sm" type="number" min="0" max="1000" step="0.001" defaultValue={p.weight ?? ''} key={`${p.id}-${p.weight}`} aria-label={`${p.name} weight in kg`} data-testid={`weight-${p.id}`}
                          onBlur={(e) => saveWeight(p, e.target.value)} placeholder="kg" />
                        : (p.weight ?? '')}
                    </td>
                  )}
                  <td>{p.inStock == null ? '' : p.inStock ? <Badge bg="success">In stock</Badge> : <Badge bg="warning" text="dark">Out of stock</Badge>}
                    {p.stockElsewhere?.length > 0 && !p.inStock && (
                      <div className="small text-muted mt-1" data-testid={`stock-elsewhere-${p.id}`}>
                        Not at the store&apos;s warehouse. {p.stockElsewhere.map((s) => `${Number(s.quantity)} in ${s.place}`).join(', ')}.
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={byWeight ? 7 : 6} className="text-center text-muted py-4">No products match.</td></tr>}
            </tbody>
          </Table>
        )}
    </div>
  );
}

/** The Online Store module: the daily work of the shop, orders first, then what is published. */
function Store() {
  const { user } = useAuth();
  const canReport = hasAnyRole(user, REPORT_ROLES);
  const [active, setActive] = useSectionTab('/store', canReport ? STORE_SECTIONS : STORE_SECTIONS.filter((k) => k !== 'reports'));
  return (
    <div>
      <Nav variant="tabs" activeKey={active} onSelect={setActive}>
        <Nav.Item><Nav.Link eventKey="orders">Orders</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="products">Products online</Nav.Link></Nav.Item>
        {canReport && <Nav.Item><Nav.Link eventKey="reports">Reports</Nav.Link></Nav.Item>}
      </Nav>
      {active === 'orders' && <OnlineOrders />}
      {active === 'products' && <ProductsOnline />}
      {active === 'reports' && canReport && <StoreReports />}
    </div>
  );
}

export default Store;
